import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '@/api/query';
import * as ep from '@/api/endpoints/finance';
import { useCan } from './useCan';
import { useCursorList } from './useCursorList';
import { useAuth } from '@/stores/auth';

export function useAccount() {
  // 侧栏余额常驻：操作员、观察员没有 finance:read，不发这个必然 403 的请求，余额位显示「—」
  const allowed = useCan('finance:read');
  return useQuery({
    queryKey: qk.finance.account(),
    queryFn: ({ signal }) => ep.getAccount(signal),
    enabled: allowed,
    // 余额挂在侧栏常驻，刷太勤没意义；写操作后精确 invalidate 即可
    staleTime: 60_000,
  });
}

export function useFlows(params: ep.FlowListParams) {
  return useCursorList({
    params,
    queryKey: (p) => qk.finance.flows(p),
    fetcher: (p, signal) => ep.listFlows(p, signal),
  });
}

export function useCreateRecharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ep.createRecharge,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.finance.account() });
    },
  });
}

const RECHARGE_FINAL = new Set(['paid', 'failed', 'expired', 'cancelled', 'refunded']);

/**
 * 充值单支付状态轮询（产品 docs/03 §5：扫码后前端轮询 status）。
 * 到终态即停；页面不可见时不停——支付回调可能在用户切走的那几秒到达，
 * 这里和任务进度的取舍不同：一笔钱的状态值得多花几个请求。
 */
export function useRechargeStatus(orderId: string | null) {
  return useQuery({
    queryKey: qk.finance.recharge(orderId ?? ''),
    queryFn: ({ signal }) => ep.getRechargeStatus(orderId as string, signal),
    enabled: Boolean(orderId),
    staleTime: 0,
    refetchInterval: (q) => (RECHARGE_FINAL.has(q.state.data?.status ?? '') ? false : 3000),
  });
}

export function useConsumeSummary(params: { from: string; to: string }) {
  return useQuery({
    queryKey: qk.finance.consume(params),
    queryFn: ({ signal }) => ep.getConsumeSummary(params, signal),
  });
}

export function useSeatSubscriptions() {
  const authed = useAuth((s) => s.me !== null);
  return useQuery({
    queryKey: qk.finance.seats(),
    queryFn: ({ signal }) => ep.listSeatSubscriptions(signal),
    enabled: authed,
  });
}

export function useUnsubscribeSeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ep.unsubscribeSeats,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.finance.seats() });
      void qc.invalidateQueries({ queryKey: ['employees', 'list'] });
    },
  });
}

export function useContracts() {
  return useCursorList({
    params: {},
    queryKey: (p) => qk.finance.contracts(p),
    fetcher: (p, signal) => ep.listContracts(p, signal),
  });
}

export function useInvoices() {
  return useCursorList({
    params: {},
    queryKey: (p) => qk.finance.invoices(p),
    fetcher: (p, signal) => ep.listInvoices(p, signal),
  });
}

export function useApplyInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ep.applyInvoice,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance', 'invoices'] }),
  });
}
